import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  ArrowDown,
  ArrowUp,
  Braces,
  Check,
  Info,
  ListChecks,
  Plus,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/select';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode, NodeStruct } from '@/types/node-types';
import {
  getValidateConfig,
  validateSchemaSubset,
  validateValidateConfig,
  type JsonValue,
  type ValidationAssertion,
} from '@/pages/workflow/helper/functional-control-flow';
import {
  assertionsToRows,
  describeValidationIssue,
  emptySchemaNode,
  inferSchemaFromSample,
  modelToSchema,
  newAssertionRow,
  previewValidation,
  rowsToAssertions,
  schemaToModel,
  suggestAssertionCode,
  suggestAssertionMessage,
  unsupportedKeywordsIn,
  type AssertionRow,
  type CheckOutcome,
  type SchemaNode,
} from '@/pages/workflow/helper/validate-builder';
import {
  collectFieldSuggestions,
  formatFieldPath,
  parseFieldPath,
  type Condition,
} from '@/pages/workflow/helper/switch-condition-builder';
import { resolveSwitchInputSample } from '@/pages/workflow/helper/switch-branch-sync';
import { ConditionEditor, DraftInput } from './switch-cases-editor';
import { JsonEditorField } from './json-editor-field';
import { SchemaNodeEditor } from './validate-schema-fields';
import { cn } from '@/lib/utils';

/**
 * The Validate / Assert node's checks as a human sees them: a **Shape** (the
 * JSON Schema subset, as a field table) and **Rules** (the assertions, as
 * rows with a code, a message and a condition). Both have "Edit as JSON";
 * both write exactly the `schema` and `assertions` the backend evaluates.
 * A preview against the upstream node's last value says what would pass.
 */

type Mode = 'builder' | 'json';

function OutcomeChip({ outcome }: { outcome: CheckOutcome | null }) {
  if (!outcome) return null;
  if (outcome.kind === 'pass') {
    return (
      <span
        className='inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300'
        data-testid='validate-check-pass'
      >
        <Check className='size-3' /> passes
      </span>
    );
  }
  return (
    <span
      className='inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300'
      data-testid='validate-check-fail'
    >
      <X className='size-3' />
      {outcome.kind === 'fail'
        ? `fails (${outcome.issues.length})`
        : 'would stop the run'}
    </span>
  );
}

function SectionHeader({
  title,
  help,
  mode,
  onToggleMode,
  children,
}: {
  title: string;
  help: string;
  mode: Mode;
  onToggleMode: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div className='flex items-center justify-between gap-2 min-h-[32px]'>
      <div className='flex items-center gap-1'>
        <Label className='text-sm'>{title}</Label>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className='h-4 w-4 text-muted-foreground cursor-help' />
          </TooltipTrigger>
          <TooltipContent>
            <p className='max-w-xs'>{help}</p>
          </TooltipContent>
        </Tooltip>
        {children}
      </div>
      <Button
        type='button'
        variant='ghost'
        size='sm'
        className='h-7 px-2 text-xs'
        data-testid={`validate-${title.toLowerCase()}-mode-toggle`}
        onClick={onToggleMode}
      >
        {mode === 'builder' ? (
          <>
            <Braces className='size-3.5' /> Edit as JSON
          </>
        ) : (
          <>
            <ListChecks className='size-3.5' /> Edit as fields
          </>
        )}
      </Button>
    </div>
  );
}

const REQUIRE_LABELS = {
  required: 'every field required',
  optional: 'every field optional',
} as const;
type RequireChoice = keyof typeof REQUIRE_LABELS;

/** "Build from the last value", with the choice of marking fields required or optional. */
function BuildFromSample({
  label,
  variant,
  requireAll,
  onRequireAll,
  onBuild,
}: {
  label: string;
  variant: 'outline' | 'ghost';
  requireAll: boolean;
  onRequireAll: (next: boolean) => void;
  onBuild: () => void;
}) {
  const choice: RequireChoice = requireAll ? 'required' : 'optional';
  return (
    <div className='flex flex-wrap items-center gap-1'>
      <Button
        type='button'
        variant={variant}
        size='sm'
        className={cn('px-2 text-xs', variant === 'outline' ? 'h-7' : 'h-6')}
        data-testid='validate-schema-from-sample'
        onClick={onBuild}
      >
        <Wand2 className='size-3' /> {label}
      </Button>
      <Select
        options={Object.values(REQUIRE_LABELS)}
        selected={REQUIRE_LABELS[choice]}
        allowAllOption={false}
        aria-label='Fields from the last value'
        className='h-7 w-auto min-w-0 text-xs px-2'
        onSelect={(picked) => onRequireAll(picked === REQUIRE_LABELS.required)}
      />
    </div>
  );
}

// --- one rule --------------------------------------------------------------

interface RuleCardProps {
  index: number;
  total: number;
  row: AssertionRow;
  outcome: CheckOutcome | null;
  codeError: string | null;
  messageError: string | null;
  suggestions: string[];
  disabled: boolean;
  onChange: (next: AssertionRow) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

function RuleCard({
  index,
  total,
  row,
  outcome,
  codeError,
  messageError,
  suggestions,
  disabled,
  onChange,
  onMove,
  onRemove,
}: RuleCardProps) {
  const onCondition = (condition: Condition) => {
    // Keep the generated code and message following the rule until the
    // author has typed their own.
    const suggested = suggestAssertionCode(row.condition, []);
    const autoCode =
      row.code === suggested ||
      row.code.startsWith(`${suggested}_`) ||
      /^check(_\d+)?$/.test(row.code);
    const autoMessage =
      row.message === suggestAssertionMessage(row.condition) ||
      row.message === 'The value must pass this check';
    onChange({
      ...row,
      condition,
      code: autoCode ? suggestAssertionCode(condition, []) : row.code,
      message: autoMessage ? suggestAssertionMessage(condition) : row.message,
    });
  };
  return (
    <div
      className={cn(
        'rounded-md border p-2 space-y-2',
        outcome?.kind === 'fail' && 'border-red-300 dark:border-red-500/60',
        outcome?.kind === 'error' &&
          'border-red-400 bg-red-50/40 dark:bg-red-950/20',
      )}
      data-testid='validate-rule'
    >
      <div className='flex items-center gap-1'>
        <span className='text-[11px] text-muted-foreground w-5 shrink-0'>
          {index + 1}.
        </span>
        <DraftInput
          value={row.code}
          disabled={disabled}
          aria-label='Rule code'
          placeholder='code, e.g. score.at_least'
          className={cn('font-mono flex-1', codeError && 'border-red-500')}
          data-testid='validate-rule-code'
          onCommit={(code) => onChange({ ...row, code: code.trim() })}
        />
        <OutcomeChip outcome={outcome} />
        {!disabled && (
          <div className='flex items-center shrink-0'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0 text-muted-foreground'
              aria-label='Move rule up'
              disabled={index === 0}
              onClick={() => onMove(-1)}
            >
              <ArrowUp className='size-3.5' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0 text-muted-foreground'
              aria-label='Move rule down'
              disabled={index === total - 1}
              onClick={() => onMove(1)}
            >
              <ArrowDown className='size-3.5' />
            </Button>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='h-7 w-7 p-0 text-muted-foreground'
              aria-label='Remove rule'
              onClick={onRemove}
            >
              <Trash2 className='size-3.5' />
            </Button>
          </div>
        )}
      </div>
      {codeError && (
        <p className='text-[11px] text-red-500 pl-5'>{codeError}</p>
      )}
      <div className='pl-5 space-y-1.5'>
        <div className='text-[11px] text-muted-foreground'>when</div>
        <ConditionEditor
          condition={row.condition}
          suggestions={suggestions}
          disabled={disabled}
          depth={0}
          onChange={onCondition}
        />
        <div className='grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1'>
          <span className='text-[11px] text-muted-foreground'>
            otherwise say
          </span>
          <DraftInput
            value={row.message}
            disabled={disabled}
            aria-label='Rule message'
            placeholder='what went wrong, in plain words'
            className={cn(messageError && 'border-red-500')}
            data-testid='validate-rule-message'
            onCommit={(message) => onChange({ ...row, message })}
          />
          <span className='text-[11px] text-muted-foreground'>about field</span>
          <DraftInput
            value={formatFieldPath(row.path)}
            disabled={disabled}
            aria-label='Rule field'
            placeholder='(the whole value)'
            className='font-mono'
            onCommit={(text) =>
              onChange({ ...row, path: parseFieldPath(text) })
            }
          />
        </div>
        {messageError && (
          <p className='text-[11px] text-red-500'>{messageError}</p>
        )}
        {outcome?.kind === 'error' && (
          <p className='text-[11px] text-red-600 dark:text-red-300'>
            {outcome.message}
          </p>
        )}
      </div>
    </div>
  );
}

// --- the editor --------------------------------------------------------------

interface ValidateChecksEditorProps {
  nodeId: string;
  struct: NodeStruct;
  disabled?: boolean;
  onChangeSchema: (schema: Record<string, JsonValue> | null) => void;
  onChangeAssertions: (assertions: ValidationAssertion[]) => void;
}

export function ValidateChecksEditor({
  nodeId,
  struct,
  disabled = false,
  onChangeSchema,
  onChangeAssertions,
}: ValidateChecksEditorProps) {
  const { node, nodes, edges } = useNodeStore(
    useShallow((state) => ({
      node: state.nodes.find((n) => n.id === nodeId) as CustomNode | undefined,
      nodes: state.nodes,
      edges: state.edges,
    })),
  );
  const [schemaMode, setSchemaMode] = useState<Mode>('builder');
  const [rulesMode, setRulesMode] = useState<Mode>('builder');
  // "Build from the last value": mark every present field required (the
  // safe default for a guard) or optional (for a loose payload).
  const [requireAll, setRequireAll] = useState(true);

  const config = useMemo(() => getValidateConfig(struct), [struct]);
  const schemaModel = useMemo<SchemaNode | null>(
    () => (config.schema ? schemaToModel(config.schema) : null),
    [config.schema],
  );
  const rows = useMemo(
    () => assertionsToRows(config.assertions),
    [config.assertions],
  );
  const errors = useMemo(() => validateValidateConfig(config), [config]);
  const schemaErrors = useMemo(
    () => (config.schema ? validateSchemaSubset(config.schema) : []),
    [config.schema],
  );
  const unsupported = useMemo(
    () => (schemaModel ? unsupportedKeywordsIn(schemaModel) : []),
    [schemaModel],
  );

  const sample = useMemo(
    () => (node ? resolveSwitchInputSample(node, nodes, edges) : undefined),
    [node, nodes, edges],
  );
  const suggestions = useMemo(() => collectFieldSuggestions(sample), [sample]);
  const upstreamName = useMemo(() => {
    const valuePortId = struct.params?.find((p) => p.field_id === 'value')
      ?.bindable_config?.input_port?.id;
    const incoming = edges.find(
      (edge) => edge.target === nodeId && edge.targetHandle === valuePortId,
    );
    const source = nodes.find((n) => n.id === incoming?.source);
    return (
      source?.data?.struct?.user_config_name ||
      source?.data?.struct?.display_name ||
      null
    );
  }, [struct.params, edges, nodes, nodeId]);
  const preview = useMemo(
    () => previewValidation(config, sample, upstreamName),
    [config, sample, upstreamName],
  );

  const commitSchema = (model: SchemaNode | null) =>
    onChangeSchema(model ? modelToSchema(model) : null);
  const commitRows = (next: AssertionRow[]) =>
    onChangeAssertions(rowsToAssertions(next));
  const errorAt = (path: string): string | null =>
    errors.find((error) => error.path === path)?.message ?? null;

  const hasAnyCheck = Boolean(config.schema) || rows.length > 0;

  return (
    <div
      className='w-full mb-4 px-3 space-y-4'
      data-testid='validate-checks-editor'
    >
      {preview.kind !== 'unknown' && hasAnyCheck && (
        <div
          className={cn(
            'rounded-md px-2 py-1.5 text-xs',
            preview.kind === 'ok' &&
              'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300',
            preview.kind === 'issues' &&
              'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
            preview.kind === 'error' &&
              'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300',
          )}
          data-testid='validate-preview'
        >
          <p>{preview.message}</p>
          {preview.kind === 'issues' && (
            <ul className='mt-1 list-disc pl-4 space-y-0.5'>
              {preview.issues.slice(0, 6).map((issue, i) => (
                <li key={i}>{describeValidationIssue(issue)}</li>
              ))}
              {preview.issues.length > 6 && (
                <li>and {preview.issues.length - 6} more</li>
              )}
            </ul>
          )}
        </div>
      )}

      {/* ---- Shape ---- */}
      <section className='space-y-2' data-testid='validate-shape'>
        <SectionHeader
          title='Shape'
          help='What the value must look like: its fields, their types, which are required, and simple bounds. Leave it empty to check only the rules below.'
          mode={schemaMode}
          onToggleMode={() =>
            setSchemaMode(schemaMode === 'builder' ? 'json' : 'builder')
          }
        >
          <OutcomeChip outcome={preview.schemaOutcome} />
        </SectionHeader>

        {schemaMode === 'json' ? (
          <JsonEditorField
            value={config.schema}
            rows={10}
            expectedShape='object'
            emptyMeansNull
            disabled={disabled}
            aria-label='Shape as JSON'
            data-testid='validate-schema-json'
            placeholder='{"type": "object", "required": ["score"], "properties": {"score": {"type": "number", "minimum": 0}}}'
            validate={(parsed) =>
              parsed === null
                ? null
                : (validateSchemaSubset(parsed)[0]?.message ?? null)
            }
            onCommit={(parsed) =>
              onChangeSchema(parsed as Record<string, JsonValue> | null)
            }
          />
        ) : schemaModel ? (
          <div className='rounded-md border p-2 space-y-2'>
            <SchemaNodeEditor
              node={schemaModel}
              depth={0}
              disabled={disabled}
              onChange={commitSchema}
            />
            {!disabled && (
              <div className='flex flex-wrap gap-1 pt-1 border-t'>
                {sample !== undefined && (
                  <BuildFromSample
                    label='Rebuild from the last value'
                    variant='ghost'
                    requireAll={requireAll}
                    onRequireAll={setRequireAll}
                    onBuild={() =>
                      commitSchema(
                        inferSchemaFromSample(sample, { requireAll }),
                      )
                    }
                  />
                )}
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs text-muted-foreground'
                  data-testid='validate-schema-remove'
                  onClick={() => commitSchema(null)}
                >
                  <Trash2 className='size-3' /> Remove shape
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className='rounded-md border border-dashed p-3 text-xs text-muted-foreground space-y-2'>
            <p>
              No shape yet. Describe the value&apos;s fields and types here, or
              check only the rules below.
            </p>
            {!disabled && (
              <div className='flex flex-wrap gap-1'>
                {sample !== undefined && (
                  <BuildFromSample
                    label='Build from the last value'
                    variant='outline'
                    requireAll={requireAll}
                    onRequireAll={setRequireAll}
                    onBuild={() =>
                      commitSchema(
                        inferSchemaFromSample(sample, { requireAll }),
                      )
                    }
                  />
                )}
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='h-7 px-2 text-xs'
                  data-testid='validate-schema-start'
                  onClick={() => commitSchema(emptySchemaNode('object'))}
                >
                  <Plus className='size-3' /> Start with an object
                </Button>
              </div>
            )}
          </div>
        )}

        {unsupported.length > 0 && (
          <ul
            className='space-y-0.5'
            role='alert'
            data-testid='validate-schema-unsupported'
          >
            {unsupported.slice(0, 4).map((entry, i) => (
              <li key={i} className='text-xs text-red-500'>
                The shape uses &quot;{entry.keyword}&quot; at {entry.path}; this
                node does not support {entry.reason}. Remove it or express it as
                a rule below.
              </li>
            ))}
          </ul>
        )}
        {schemaErrors.length > 0 && unsupported.length === 0 && (
          <ul className='space-y-0.5' role='alert'>
            {schemaErrors.slice(0, 3).map((error, i) => (
              <li key={i} className='text-xs text-red-500'>
                {error.message}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---- Rules ---- */}
      <section className='space-y-2' data-testid='validate-rules'>
        <SectionHeader
          title='Rules'
          help='Each rule is a condition the value must satisfy. When it does not, its code and message are recorded in the error list (and the run stops, if that is the mode).'
          mode={rulesMode}
          onToggleMode={() =>
            setRulesMode(rulesMode === 'builder' ? 'json' : 'builder')
          }
        />

        {rulesMode === 'json' ? (
          <JsonEditorField
            value={config.assertions}
            rows={12}
            expectedShape='array'
            disabled={disabled}
            aria-label='Rules as JSON'
            data-testid='validate-assertions-json'
            placeholder='[{"code": "score.at_least", "message": "score must be at least 5", "path": ["score"], "when": {"op": "gte", "left": {"op": "path", "path": ["value", "score"]}, "right": {"op": "literal", "value": 5}}}]'
            validate={(parsed) => {
              const assertions = parsed as ValidationAssertion[];
              if (
                !assertions.every(
                  (entry) =>
                    entry &&
                    typeof entry === 'object' &&
                    typeof entry.code === 'string' &&
                    typeof entry.message === 'string' &&
                    entry.when,
                )
              ) {
                return 'Each rule needs a "code", a "message" and a "when" condition.';
              }
              return (
                validateValidateConfig({ ...config, assertions }).filter((e) =>
                  e.path.startsWith('assertions'),
                )[0]?.message ?? null
              );
            }}
            onCommit={(parsed) =>
              onChangeAssertions(parsed as ValidationAssertion[])
            }
          />
        ) : (
          <div className='space-y-2'>
            {rows.map((row, index) => (
              <RuleCard
                key={index}
                index={index}
                total={rows.length}
                row={row}
                outcome={preview.assertionOutcomes[index] ?? null}
                codeError={errorAt(`assertions[${index}].code`)}
                messageError={errorAt(`assertions[${index}].message`)}
                suggestions={suggestions}
                disabled={disabled}
                onChange={(next) =>
                  commitRows(rows.map((r, i) => (i === index ? next : r)))
                }
                onMove={(direction) => {
                  const target = index + direction;
                  if (target < 0 || target >= rows.length) return;
                  const next = [...rows];
                  [next[index], next[target]] = [next[target], next[index]];
                  commitRows(next);
                }}
                onRemove={() => commitRows(rows.filter((_, i) => i !== index))}
              />
            ))}
            {!disabled && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='h-8 text-xs w-full'
                data-testid='validate-add-rule'
                onClick={() =>
                  commitRows([
                    ...rows,
                    newAssertionRow(rows.map((r) => r.code)),
                  ])
                }
              >
                <Plus className='size-3.5' /> Add rule
              </Button>
            )}
          </div>
        )}
      </section>

      {errors.filter(
        (e) => !e.path.startsWith('assertions') && !e.path.startsWith('schema'),
      ).length > 0 && (
        <ul className='space-y-0.5' role='alert'>
          {errors
            .filter(
              (e) =>
                !e.path.startsWith('assertions') &&
                !e.path.startsWith('schema'),
            )
            .slice(0, 3)
            .map((error, i) => (
              <li key={i} className='text-xs text-red-500'>
                {error.message}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
